import React, { useState } from 'react';
import { Button, CircularProgress, Tooltip, useTheme, Popper, Paper, Box, Typography } from '@mui/material';
import { useAppDispatch } from 'src/store';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { conversationAutocompleteNow, updateConversationDeliveryState, updateConversationStatus } from 'src/features/account/conversations.slice';

interface GenerateReplyButtonProps {
  conversation: any;
}

const GenerateReplyButton: React.FC<GenerateReplyButtonProps> = ({ conversation }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const [localLoading, setLocalLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  
  const isDisabledStatus = ['closed', 'needs_attention', 'standby'].includes(conversation.status);
  
  const handleClick = async () => {
    if (localLoading) return;

    // Prevent multiple generations simultaneously unless user confirms.
    if (conversation.autocomplete_status === 'processing_autocomplete') {
      const proceed = window.confirm('A response is already being generated for this conversation. Generate another?');
      if (!proceed) return;
    }

    setLocalLoading(true);

    try {
      // Pause auto-delivery so the draft can be reviewed first.
      // if (conversation.delivery_state !== 'pause') {
      //   await dispatch(updateConversationDeliveryState({
      //     conversationId: conversation.id,
      //     channelId: conversation.channel_id,
      //     deliveryState: 'pause'
      //   }));
      // }

      await dispatch(conversationAutocompleteNow({
        conversationId: conversation.id,
        channelId: conversation.channel_id
      }));
    } finally {
      setLocalLoading(false);
    }
  };

  const handleButtonMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    if (isDisabledStatus) {
      if (timeoutId) clearTimeout(timeoutId);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleButtonMouseLeave = () => {
    if (isDisabledStatus) {
      timeoutId = setTimeout(() => {
        setAnchorEl(null);
      }, 200);
    }
  };

  const handlePopperMouseEnter = () => {
    if (timeoutId) clearTimeout(timeoutId);
  };

  const handlePopperMouseLeave = () => {
    timeoutId = setTimeout(() => {
      setAnchorEl(null);
    }, 200);
  };

  // Derive status details
  const status = conversation.autocomplete_status;
  const isGenerating = status === 'processing_autocomplete';
  const isQueued = status === 'queued';
  const isError = status === 'error';
  const isSuccess = status === 'completed';

  const buttonDisabled = localLoading || isGenerating || isQueued || isDisabledStatus;

  const { label, icon, color } = (() => {
    if (localLoading || isGenerating) {
      return {
        label: 'Generating…',
        icon: <CircularProgress size={18} color="inherit" />,
        color: 'primary' as const
      };
    }
    if (isQueued) {
      return {
        label: 'Queued',
        icon: <PendingActionsIcon fontSize="small" />,
        color: 'info' as const
      };
    }
    if (isError) {
      return {
        label: 'Error',
        icon: <ErrorIcon fontSize="small" />,
        color: 'error' as const
      };
    }
    if (isSuccess) {
      return {
        label: 'Draft Ready',
        icon: <CheckCircleIcon fontSize="small" />,
        color: 'success' as const
      };
    }
    return {
      label: 'Generate Reply',
      icon: <AutoAwesomeIcon fontSize="small" />,
      color: 'primary' as const
    };
  })();

  let finalLabel = label;
  let finalIcon = icon;
  let finalColor = color;

  if (isDisabledStatus && !localLoading && !isGenerating && !isQueued) {
    finalLabel = 'Generate Reply';
    finalIcon = <ErrorIcon fontSize="small" />;
    finalColor = 'error';
  }
  
  const buttonElement = (
    <Box
      component="span"
      onMouseEnter={handleButtonMouseEnter}
      onMouseLeave={handleButtonMouseLeave}
    >
      <Button
        variant="contained"
        color={finalColor}
        size="small"
        startIcon={finalIcon}
        disabled={buttonDisabled}
        onClick={handleClick}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 2,
          px: 1.5,
          minWidth: 140,
          boxShadow: theme.shadows[2],
        }}
      >
        {finalLabel}
      </Button>
    </Box>
  );

  return (
    <>
      {isDisabledStatus ? buttonElement : (
        <Tooltip
          arrow
          title="Ask Oppy to draft a suggested reply using the latest conversation context. Auto-delivery will pause so you can edit before sending."
        >
          {buttonElement}
        </Tooltip>
      )}
      <Popper open={Boolean(anchorEl)} anchorEl={anchorEl} placement="bottom">
        <Paper
          elevation={3}
          sx={{ p: 2, maxWidth: 300 }}
          onMouseEnter={handlePopperMouseEnter}
          onMouseLeave={handlePopperMouseLeave}
        >
          <Typography variant="body2">
            AI generation is disabled for conversations in &quot;{conversation.status}&quot; status.
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => {
                dispatch(updateConversationStatus({
                  conversationId: conversation.id,
                  status: 'active',
                  channelId: conversation.channel_id,
                }));
                setAnchorEl(null);
              }}
            >
              Change to Active
            </Button>
          </Box>
        </Paper>
      </Popper>
    </>
  );
};

export default GenerateReplyButton;
